import React, { useState } from "react";
import { resetPassword } from "./auth";
import "./Login.css";
import { useTranslation } from "react-i18next";

function ResetForm({ email, setIsResetPass }) {
  const { t } = useTranslation("login");
  const [formData, setFormData] = useState({
    password: "",
    passwordConfirm: ""
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetPassError, setResetPassError] = useState("");

  const validate = (data) => {
    const errs = {};
    if (!data.password) {
      errs.password = t("resetForm.validation.passwordRequired");
    } else if (data.password.length < 8) {
      errs.password = t("resetForm.validation.passwordMinLength");
    } else if (!/[A-Z]/.test(data.password)) {
      errs.password = t("resetForm.validation.passwordUppercase");
    } else if (!/[^A-Za-z0-9]/.test(data.password)) {
      errs.password = t("resetForm.validation.passwordSpecialChar");
    }
    if (!data.passwordConfirm) {
      errs.passwordConfirm = t("resetForm.validation.confirmPasswordRequired");
    } else if (data.passwordConfirm !== data.password) {
      errs.passwordConfirm = t("resetForm.validation.passwordsMismatch");
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...formData, [name]: value };
    setFormData(newData);
    setErrors(validate(newData));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(formData);
    setErrors(validationErrors);
    setTouched({ password: true, passwordConfirm: true });
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    const values = { ...formData, email };
    try {
      setIsPasswordReset(false);
      setResetPassError("");
      setLoading(true);
      const response = await resetPassword(values);
      if (response?.status === 200 || response?.status === 201) {
        setIsPasswordReset(true);
      }
    } catch (error) {
      const resp = error.response;
      if (resp?.errors && Array.isArray(resp.errors)) {
        const backendErrors = {};
        resp.errors.forEach((errObj) => {
          const key = Object.keys(errObj)[0];
          backendErrors[key] = errObj[key];
        });
        setErrors((prev) => ({ ...prev, ...backendErrors }));
        const newTouched = {};
        Object.keys(backendErrors).forEach((key) => {
          newTouched[key] = true;
        });
        setTouched((prev) => ({ ...prev, ...newTouched }));
      } else {
        const errorMessage =
          resp?.error ||
          resp?.message ||
          t("resetForm.genericError");
        setResetPassError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (isPasswordReset) {
    return (
      <div className="success-message">
        <img src="/tick-circle.svg" alt={t("resetForm.alt.success")} />
        <h1 className="title-login">
          {t("resetForm.success.title")}
        </h1>
        <p className="subtitle-login">
          {t("resetForm.success.subtitle")}
        </p>
        <button className="login-btn" onClick={() => setIsResetPass(false)}>
          {t("resetForm.buttons.continueLogin")}
        </button>
      </div>
    );
  }

  return (
    <>
      <h2 className="title-login">{t("resetForm.title")}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group-unique">
          <label htmlFor="password">{t("resetForm.labels.newPassword")}</label>
          <div className="password-input-unique">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <span
              className="toggle-password-unique"
              onClick={togglePasswordVisibility}
            >
              <img
                src={showPassword ? "/eye.svg" : "/eye-slash.svg"}
                alt={t("resetForm.alt.toggleVisibility")}
              />
            </span>
          </div>
          {touched.password && (errors.password || resetPassError) && (
            <p className="login-error">
              {errors.password || resetPassError}
            </p>
          )}
        </div>
        <div className="form-group-unique">
          <label htmlFor="passwordConfirm">
            {t("resetForm.labels.confirmPassword")}
          </label>
          <div className="password-input-unique">
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="passwordConfirm"
              name="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <span
              className="toggle-password-unique"
              onClick={toggleConfirmPasswordVisibility}
            >
              <img
                src={showConfirmPassword ? "/eye.svg" : "/eye-slash.svg"}
                alt={t("resetForm.alt.toggleVisibility")}
              />
            </span>
          </div>
          {touched.passwordConfirm && errors.passwordConfirm && (
            <p className="login-error">
              {errors.passwordConfirm}
            </p>
          )}
        </div>
        <div className="form-group-login">
          <button
            disabled={
              loading ||
              Object.keys(errors).length > 0 ||
              !formData.password ||
              !formData.passwordConfirm
            }
            className={"login-btn"}
            type="submit"
          >
            {loading
              ? t("resetForm.buttons.submitting")
              : t("resetForm.buttons.reset")}
          </button>
        </div>
      </form>
    </>
  );
}

export default ResetForm;
